import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { Icon as LayerIcon } from '@musinsa/pbo-assets'
import Text from '@/components/content/Text'
import styled from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import Input from '@/components/content/Input'
import { useState } from 'react'
import { AxiosError } from 'axios'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { useUpdateSlotWaitTime } from '@/services/outboundSchedule2/useOutboundScheduleQueries'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { SlotView } from '@/services/outboundSchedule2/types'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { formatWaitTime } from '../../utils/formatWaitTime'
import { MAX_WAIT_TIME, MIN_WAIT_TIME, WAIT_TIME_STEP } from '@/pages/outbound/constants'

interface Props {
  time: string
  waitTime?: number
  schedule: SlotView
  onClose: () => void
  onSuccess?: () => void
}

export function SetWaitTimeModal({ time, waitTime, schedule, onClose, onSuccess }: Props) {
  const [value, setValue] = useState<number | ''>(waitTime ?? 60)

  const { shipperCode, cutOffDate } = useCutOffDate()
  const updateSlotWaitTime = useUpdateSlotWaitTime()

  const isValid = typeof value === 'number' && value >= MIN_WAIT_TIME && value <= MAX_WAIT_TIME && value % WAIT_TIME_STEP === 0

  const handleChange = (raw: string) => {
    if (raw === '') {
      setValue('')
      return
    }
    if (!/^\d+$/.test(raw)) return
    const num = Number(raw)
    if (num > MAX_WAIT_TIME) return
    setValue(num)
  }

  const handleSubmit = async () => {
    if (typeof value !== 'number') return

    try {
      await updateSlotWaitTime.mutateAsync({
        shipperCode,
        cutType: 'B2C',
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        waitTimeMinutes: value,
        applyDate: cutOffDate,
      })
      MToast.show(`출고대기시간이 ${formatWaitTime(value)}로 저장되었습니다.`)
      onClose()
      onSuccess?.()
    } catch (e) {
      MAlert.show(getShipperScopeErrorMessage(e) ?? Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '출고대기시간 설정에 실패했습니다.'))
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <HStack gap={8} align="center">
          <Text kind="Headline_20_bold">출고대기시간 변경</Text>
          <Text kind="Headline_16_regular" color={colorSystem.text.common.secondary}>
            {time}
          </Text>
        </HStack>
      }
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '저장', onClick: handleSubmit, disabled: !isValid },
      ]}
    >
      <DescriptionBox gap={8} align="flex-start" style={{ marginBottom: 24 }}>
        <IconWrap>
          <LayerIcon name="ic-infoCircle" size={18} filled color={colors.gray500} />
        </IconWrap>
        <Text kind="Body_14_regular" color={tokens.fg} style={{ fontFamily: "'Pretendard', sans-serif" }}>
          출고 대기 시간은 MOMS 주문 수집 후 합포를 위해 대기하는 시간을 의미합니다.
        </Text>
      </DescriptionBox>

      <VStack gap={4}>
        <HStack gap={16} align="center">
          <Label kind="Body_14_medium">출고대기시간</Label>
          <InputWrap>
            <Input size="sm" block placeholder="분 입력" value={value === '' ? '' : String(value)} onChange={handleChange} />
          </InputWrap>
          <Text kind="Body_14_regular">분 (M) · 10분 단위</Text>
        </HStack>
        {value !== '' && typeof value === 'number' && value < MIN_WAIT_TIME && (
          <Text kind="Body_12_medium" color={colorSystem.text.common.error}>
            10분 이상으로 입력해 주세요.
          </Text>
        )}
        {value !== '' && typeof value === 'number' && value >= MIN_WAIT_TIME && value % WAIT_TIME_STEP !== 0 && (
          <Text kind="Body_12_medium" color={colorSystem.text.common.error}>
            10분 단위로 입력해 주세요.
          </Text>
        )}
      </VStack>
    </Modal>
  )
}

const Label = styled(Text)`
  min-width: 100px;
`

const InputWrap = styled.div`
  width: 120px;
`

const DescriptionBox = styled(HStack)`
  padding: 16px 12px;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  background-color: rgba(224, 224, 224, 0.2);
`

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
  flex-shrink: 0;
`
